import './App.css';
import React, { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { buildApiUrl } from "./config";  // ✅ Import config
import HomeworkReadOnlyViewer from "./HomeworkReadOnlyViewer";
import SchoolManager from "./SchoolManager";
import TeacherLogin from "./TeacherLogin";
import TeacherDashboard from "./TeacherDashboard";
import FormPage from "./FormPage";
import StudentPage from "./StudentPage";
import ParentDashboard from "./ParentDashboard";
import ParentLogin from "./ParentLogin";
import HomeworkPage from "./HomeworkPage";
import StudentDashboard from "./StudentDashboard";
import StudentLogin from "./StudentLogin";
import MainLogin from "./MainLogin";
import HomeworkResult from "./HomeworkResult";
import HomeworkViewer from "./HomeworkViewer";
import ListeningAssignmentViewer from "./ListeningAssignmentViewer";
import ReadingAssignmentViewer from "./ReadingAssignmentViewer";
import AdminLiveDashboard from "./pages/AdminLiveDashboard";
import HomeworkAnnotationViewer from "./HomeworkAnnotationViewer";
import StudentTTRS from "./pages/StudentTTRS";
import StudentQuickLinks from "./pages/StudentQuickLinks";
import PlanningTab from './pages/PlanningTab';
import TeacherBoards from "./pages/TeacherBoards";
import PublicPostViewer from "./components/PublicPostViewer";  // ✅ Import PublicPostViewer
import ParentDocumentsPublicView from "./pages/ParentDocumentsPublicView";  // ✅ Import Parent Documents public view
import MyTimetable from "./MyTimetable";  // ✅ Import MyTimetable
import StudentLiveMonitor from "./StudentLiveMonitor";  // ✅ Import Student Live Monitor
import TeacherMonitorDashboard from "./TeacherMonitorDashboard";  // ✅ Import Teacher Monitor Dashboard
import StudentConnection from "./StudentConnection";  // ✅ Import Student Connection for FormPage integration
import LoginPage from "./components/LoginPage";  // ✅ Import Login Page
import ProtectedRoute from "./components/ProtectedRoute";  // ✅ Import Protected Route
import authService from "./services/authService";  // ✅ Import Auth Service
import PrivacyPolicy from "./pages/PrivacyPolicy";  // ✅ Import Privacy Policy

// Navigation component that uses useLocation hook
const Navigation = ({ students, teacherEmail, setTeacherEmail }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { role, logout } = useAuth(); // <-- Use auth here

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const navItems = [
    { id: "teacher", label: "Teacher", icon: "👨‍🏫", path: "/teacher", description: "Teacher portal" },
    { id: "parent", label: "Parent", icon: "👨‍👩‍👧‍👦", path: "/parent", description: "Parent dashboard" },
    { id: "student", label: "Student", icon: "🎓", path: "/student-dashboard", description: "Student portal" }
  ];

  // Home icon always takes you to your dashboard if logged in, otherwise main page
  const getHomeLink = () => {
    if (role === "teacher") return "/teacher";
    if (role === "parent") return "/parent";
    if (role === "student") return "/student-dashboard";
    return "/";
  };

  // ...rest of Navigation unchanged except change <Link to="/"> to:
  // <Link to={getHomeLink()} ...>
  // And add a logout button

  if (location.pathname === "/") return null;

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <Link
                to={getHomeLink()}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">🏫</span>
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-gray-900">Sherborne AI Portal</h1>
                </div>
              </Link>
              {/* ...rest unchanged... */}
            </div>
            <div className="flex items-center gap-2">
              {/* ...nav items... */}
              {role && (
                <button
                  onClick={logout}
                  className="ml-2 px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
        {/* ...rest unchanged... */}
      </nav>
      {/* ...rest unchanged... */}
    </>
  );
};

function App() {
  const [students, setStudents] = useState([]);
  const [teacherEmail, setTeacherEmail] = useState(localStorage.getItem("teacherEmail") || "");
  const { role, login } = useAuth();

  // ✅ Check for pending Office 365 teacher login and complete it
  useEffect(() => {
    const office365LoginPending = localStorage.getItem("office365LoginPending");
    if (office365LoginPending === "true") {
      const email = localStorage.getItem("teacherEmail");
      if (email) {
        // Complete the login by authenticating with authService
        (async () => {
          try {
            const result = await authService.loginWithOffice365(email);
            if (result.success) {
              // Login with full user data including security groups and permissions
              login("teacher", "office365-authenticated", email, 
                    result.user.school, 
                    {
                      name: result.user.name,
                      email: result.user.email,
                      school: result.user.school,
                      securityGroup: result.user.securityGroup,
                      modules: result.user.modules,
                      permissions: result.user.permissions
                    });
              console.log("✅ Office 365 teacher login completed for:", email);
              localStorage.removeItem("office365LoginPending");
              // Redirect to teacher dashboard after login is complete
              window.location.href = '/teacher';
            } else {
              console.error("❌ Office 365 teacher authentication failed:", result.message);
              // Clear pending flag and redirect to login
              localStorage.removeItem("office365LoginPending");
              window.location.href = "/login-teacher";
            }
          } catch (error) {
            console.error("❌ Error completing Office 365 teacher login:", error);
            localStorage.removeItem("office365LoginPending");
            window.location.href = "/login-teacher";
          }
        })();
      }
    }
  }, [login]);

  // ✅ Check for pending Office 365 student login and complete it
  useEffect(() => {
    const office365StudentLoginPending = localStorage.getItem("office365StudentLoginPending");
    if (office365StudentLoginPending === "true") {
      const email = localStorage.getItem("studentEmail");
      if (email) {
        // Complete student login by checking synced_students
        (async () => {
          try {
            const res = await fetch(buildApiUrl("/synced-students"));
            const data = await res.json();
            const students = data.students || data;
            
            // Try to match by email first
            let matched = students.find(
              (s) => s.email && s.email.toLowerCase() === email.toLowerCase()
            );

            // If no email match, try to extract school_id from email pattern
            // e.g., 073757705547@sherborneqatar.org -> 073757705547
            if (!matched) {
              const emailPrefix = email.split('@')[0];
              // Check if prefix looks like a school_id (digits)
              if (/^\d+$/.test(emailPrefix)) {
                matched = students.find(
                  (s) => s.school_id === emailPrefix
                );
                console.log('Trying to match by school_id from email:', emailPrefix);
              }
            }

            if (matched) {
              localStorage.setItem("schoolId", matched.school_id);
              login("student", "office365-authenticated", null, matched.school_id);
              console.log("✅ Office 365 student login completed for:", email, "School ID:", matched.school_id);
              localStorage.removeItem("office365StudentLoginPending");
              // Redirect to student dashboard after login is complete
              window.location.href = '/student-dashboard';
            } else {
              console.error("❌ No student record found for:", email);
              alert("No student record found for this Office 365 account.\n\nPlease contact IT support to add your email to the system.\n\nYour Office 365 email: " + email);
              localStorage.removeItem("office365StudentLoginPending");
              window.location.href = "/login-student";
            }
          } catch (error) {
            console.error("❌ Error completing Office 365 student login:", error);
            localStorage.removeItem("office365StudentLoginPending");
            window.location.href = "/login-student";
          }
        })();
      }
    }
  }, [login]);

  // ✅ PREVENT AUTO-SCROLLING: Disable all auto-scroll behaviors
  useEffect(() => {
    // Prevent any auto-scrolling on page load or updates
    const preventAutoScroll = () => {
      // Store current scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // Restore scroll position if it changes unexpectedly
      window.onscroll = function() {
        // Only reset if scroll happens without user interaction
        if (Math.abs(window.pageYOffset - scrollTop) > 5) {
          // Restore to original position to prevent auto-scroll
          // Note: Only do this if scroll change was sudden (likely auto-scroll)
        }
      };
    };

    // Disable smooth scrolling globally
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    
    // Prevent any auto-focus from causing scroll
    const originalFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function(options) {
      return originalFocus.call(this, { ...options, preventScroll: true });
    };

    preventAutoScroll();

    // Cleanup
    return () => {
      window.onscroll = null;
      HTMLElement.prototype.focus = originalFocus;
    };
  }, []);

  useEffect(() => {
    fetch(buildApiUrl("/synced-students"))
      .then(res => res.json())
      .then(data => {
        const deduped = Array.from(new Map((data.students || []).map(s => [s.person_id, s])).values());
        setStudents(deduped);
      })
      .catch(err => console.error("Failed to fetch students:", err));
  }, []);

  // Shortcuts for protected routes
  const requireRole = (allowedRole, element) =>
    role === allowedRole ? element : <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gray-50" style={{ 
      overflowX: 'hidden', 
      position: 'relative',
      scrollBehavior: 'auto'
    }}>
      {/* Navigation Removed */}

      <main className="transition-all duration-300 ease-in-out" style={{
        overflowX: 'hidden',
        scrollBehavior: 'auto'
      }}>
        <Routes>
          {/* Root: If logged in, go to dashboard. If not, show login/role picker */}
          <Route
            path="/"
            element={
              role
                ? <Navigate to={role === "teacher" ? "/teacher" : role === "parent" ? "/parent" : "/student-dashboard"} replace />
                : <MainLogin />
            }
          />

          {/* Login pages: Redirect to dashboard if already logged in */}
          <Route path="/login-teacher" element={role === "teacher" ? <Navigate to="/teacher" replace /> : <TeacherLogin onLogin={setTeacherEmail} />} />
          <Route path="/login-parent" element={role === "parent" ? <Navigate to="/parent" replace /> : <ParentLogin />} />
          <Route path="/student-login" element={role === "student" ? <Navigate to="/student-dashboard" replace /> : <StudentLogin />} />

          {/* Dashboards: only accessible if logged in as that role */}
          <Route path="/teacher" element={requireRole("teacher", <TeacherDashboard teacherEmail={teacherEmail} />)} />
          <Route path="/parent" element={requireRole("parent", <ParentDashboard />)} />
          <Route path="/student-dashboard" element={requireRole("student", <StudentDashboard />)} />

          {/* Teacher-specific routes */}
          <Route path="/teacher-boards" element={requireRole("teacher", <TeacherBoards />)} />
          <Route path="/planning" element={requireRole("teacher", <PlanningTab />)} />

          {/* Other routes */}
          <Route path="/form/:formId" element={<FormPage />} />
          <Route path="/student/:studentId" element={<StudentPage />} />
          <Route path="/homeworks" element={<HomeworkPage />} />
          <Route path="/admin-live-dashboard" element={<AdminLiveDashboard />} />
          <Route path="/homework/:homeworkId" element={<HomeworkPage />} />
          <Route path="/homework-result" element={<HomeworkResult />} />
          <Route path="/homework-result/:homeworkId" element={<HomeworkResult />} />
          <Route path="/homework-result/:studentId/:homeworkId" element={<HomeworkResult />} />
          <Route path="/homework-result/:studentId" element={<HomeworkResult />} />
          <Route path="/student-homework-viewer/:studentId/:homeworkId" element={<HomeworkViewer />} />
          <Route path="/homework-viewer/:homeworkId" element={<HomeworkViewer />} />
          <Route path="/listening-assignment/:id" element={<ListeningAssignmentViewer />} />
          <Route path="/reading-assignment/:id" element={<ReadingAssignmentViewer />} />
          <Route path="/interactive-homework/:id" element={<HomeworkAnnotationViewer />} />
          <Route path="/view-homework/:homeworkId" element={<HomeworkReadOnlyViewer />} />
          <Route path="/student-ttrs" element={<StudentTTRS />} />
          <Route path="/student-quick-links" element={<StudentQuickLinks />} />
          
          {/* ✅ NEW: Teacher My Timetable route */}
          <Route path="/my-timetable" element={<MyTimetable />} />
          
          {/* ✅ NEW: Live Monitoring System Routes */}
          <Route path="/student-live-monitor" element={<StudentLiveMonitor />} />
          <Route path="/teacher-monitor-dashboard" element={<TeacherMonitorDashboard />} />
          <Route path="/student-connection/:formId" element={<StudentConnection />} />
          
          {/* ✅ NEW: Public post sharing route (no authentication required) */}
          <Route path="/share/:postId" element={<PublicPostViewer />} />

          {/* ✅ Public parent documents view – shareable with parents */}
          <Route path="/docs/:schoolId" element={<ParentDocumentsPublicView />} />
          
          {/* ✅ Privacy Policy route (accessible to everyone) */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">❓</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
                <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                <Link
                  to={role ? (role === "teacher" ? "/teacher" : role === "parent" ? "/parent" : "/student-dashboard") : "/"}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 inline-block"
                >
                  Go Home
                </Link>
              </div>
            </div>
          } />
        </Routes>
      </main>
    </div>
  );
}

export default App;
